// 評価欄パネル（§6-2）。経営側は編集、それ以外には表示しない（呼び出し側で制御）。
import { useState } from "react";
import { Button, Field, SelectInput, TextInput } from "../../components/ui";
import {
  CLASSIFICATIONS,
  HQ_REVIEW_STATUS,
  PL_IMPACTS,
  RISK_LEVELS,
} from "../../masters";
import { api, ApiError } from "../../lib/api";
import { t } from "../../i18n/zh-TW";
import type { Contract, EvaluationValues } from "../../lib/types";

function toValues(c: Contract): EvaluationValues {
  return {
    pl_impact: c.pl_impact ?? "",
    pl_impact_note: c.pl_impact_note ?? "",
    risk_level: c.risk_level ?? "",
    classification: c.classification ?? "",
    hq_review_status: c.hq_review_status ?? "",
  };
}

export function EvaluationPanel({
  contract,
  onChanged,
}: {
  contract: Contract;
  onChanged: (updated: Contract) => void;
}) {
  const [values, setValues] = useState<EvaluationValues>(toValues(contract));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState(false);

  const isC = values.classification === "C";

  function set<K extends keyof EvaluationValues>(key: K, val: EvaluationValues[K]) {
    setValues((v) => ({ ...v, [key]: val }));
    setSavedAt(false);
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.updateEvaluation(contract.id, values);
      // サーバ正規化後の値（C類以外なら本社確認がクリア等）でフォームを再同期。
      setValues(toValues(res.contract));
      onChanged(res.contract);
      setSavedAt(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t.errors.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t.evaluation.classification}>
          <SelectInput
            options={CLASSIFICATIONS}
            placeholder={t.placeholders.select}
            value={values.classification}
            onChange={(e) => {
              const code = e.target.value;
              setValues((v) => ({
                ...v,
                classification: code,
                // C類以外にしたら本社確認ステータスは消す（不整合防止）。
                hq_review_status: code === "C" ? v.hq_review_status : "",
              }));
              setSavedAt(false);
            }}
          />
        </Field>
        <Field label={t.evaluation.riskLevel}>
          <SelectInput
            options={RISK_LEVELS}
            placeholder={t.placeholders.select}
            value={values.risk_level}
            onChange={(e) => set("risk_level", e.target.value)}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t.evaluation.plImpact}>
          <SelectInput
            options={PL_IMPACTS}
            placeholder={t.placeholders.select}
            value={values.pl_impact}
            onChange={(e) => set("pl_impact", e.target.value)}
          />
        </Field>
        <Field label={t.evaluation.plImpactNote}>
          <TextInput
            value={values.pl_impact_note}
            onChange={(e) => set("pl_impact_note", e.target.value)}
          />
        </Field>
      </div>

      {isC && (
        <Field label={t.evaluation.hqReviewStatus} hint={t.evaluation.hqOnlyForC}>
          <SelectInput
            options={HQ_REVIEW_STATUS}
            placeholder={t.placeholders.select}
            value={values.hq_review_status}
            onChange={(e) => set("hq_review_status", e.target.value)}
          />
        </Field>
      )}

      {error && <p className="text-sm text-red-700">{error}</p>}

      <div className="flex items-center justify-end gap-3">
        {savedAt && <span className="text-sm text-emerald-600">{t.evaluation.saved}</span>}
        <Button disabled={busy} onClick={save}>
          {t.evaluation.save}
        </Button>
      </div>
    </div>
  );
}
